import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import { FormattedMessage } from 'react-intl';
import React, { FC, ReactNode, useCallback, useState } from 'react';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Page from '@oberoninternal/travelbase-website/dist/components/Page';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import { gql } from '@apollo/client';
import {
    ContactDocument,
    ContactGlobalsFragment,
    ContactMatrixFragment,
    ContactQuery,
    ContactQueryVariables,
    ContentMatrixFragment,
    useContactMeQuery,
} from '../../generated/graphql';
import Whatsapp from '../icons/social/Whatsapp';
import { GetStaticProps } from 'next';
import initApolloClient, { PreviewData } from '../../initApolloClient';
import { addApolloState } from '@oberoninternal/travelbase-website/dist/createApolloClient';
import HtmlMapper from 'react-html-map';
import tagMap from '../../constants/tagMap';
import LinkField from '../LinkField';
import styled from 'styled-components';
import { Box, Flex } from '@rebass/grid';
import Phone from '../icons/social/Phone';
import Mail from '../icons/social/Mail';
import Link from '../Link';
import VVV from '../icons/social/VVV';
import My from '../icons/social/My';
import Messenger from '../icons/social/Messenger';
import Seo from '../Seo';
import ContentMatrix from '../ContentMatrix';
import ContentWrapper from '@oberoninternal/travelbase-website/dist/components/ContentWrapper';
import useAnalyticsContentCategory from '@oberoninternal/travelbase-website/dist/hooks/analytics/useAnalyticsContentCategory';
import { heroTransformToProps } from '../../constants/imageTransforms';
import Hero from '../Hero';
import LoginModal from '../LoginModal';
import addGlobalQuery from '../../utils/addGlobalQuery';
import { DEFAULT_REVALIDATION_TIME } from '../../constants/revalidationTime';
import { WHATSAPP_NUMBER } from '../../constants/brandConfig';
import hasRedirect from '../../utils/hasRedirect';

/* eslint-disable react/jsx-no-target-blank */

interface ContactOption {
    name: ReactNode;
    icon: ReactNode;
    globalKey: keyof ContactGlobalsFragment;
    matrixKey: keyof ContactMatrixFragment;
    link: ReactNode | null;
}

const MyLink: FC<React.PropsWithChildren<{ onClick?: React.MouseEventHandler }>> = ({ onClick, children }) => (
    <Link href={`/my`}>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
        <a onClick={onClick}>{children}</a>
    </Link>
);
const contactOptions: ContactOption[] = [
    {
        name: <FormattedMessage defaultMessage="WhatsApp" />,
        icon: <Whatsapp width={24} height={24} />,
        globalKey: 'contactWhatsappText',
        matrixKey: 'whatsapp',
        link: (
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank">
                +{[WHATSAPP_NUMBER.slice(0, 2), WHATSAPP_NUMBER.slice(2, 5), WHATSAPP_NUMBER.slice(5)].join(' ')}
            </a>
        ),
    },
    {
        name: <FormattedMessage defaultMessage="Telefoon" />,
        icon: <Phone width={24} height={24} />,
        globalKey: 'contactPhoneText',
        matrixKey: 'phone',
        link: (
            <a href="tel:+31222314741">
                <FormattedMessage defaultMessage="+31 222 314741" />
            </a>
        ),
    },
    {
        name: <FormattedMessage defaultMessage="E-mail" />,
        icon: <Mail width={24} height={24} />,
        globalKey: 'contactEmailText',
        matrixKey: 'email',
        link: <a href="mailto:info@texel.net">info@texel.net</a>,
    },
    {
        name: <FormattedMessage defaultMessage="Messenger" />,
        icon: <Messenger width={24} height={24} />,
        globalKey: 'contactMessengerText',
        matrixKey: 'messenger',
        link: (
            <a href="https://m.me/vvvtexel" target="_blank">
                <FormattedMessage defaultMessage="Start chat" />
            </a>
        ),
    },
    {
        name: <FormattedMessage defaultMessage="Mijn Texel" />,
        icon: <My width={24} height={24} />,
        globalKey: 'contactMyTexelText',
        matrixKey: 'myTexel',
        // gets added by page itself
        link: null,
    },
    {
        name: <FormattedMessage defaultMessage="Winkel VVV Texel" />,
        icon: <VVV width={24} height={24} />,
        globalKey: 'contactShopText',
        matrixKey: 'shop',
        link: (
            <a href="https://goo.gl/maps/z1c68u44zKKQeQX28" target="_blank">
                <FormattedMessage defaultMessage="Routebeschrijving" />
            </a>
        ),
    },
];

export const query = gql`
    query Contact($lang: String) {
        globalSet(handle: "contact", site: [$lang]) {
            ...ContactGlobals
        }

        entry(section: "contact", site: [$lang]) {
            ...Contact
        }
    }

    # seperate query, because this info can only be fetched clientside
    query ContactMe {
        viewer {
            id
        }
    }

    fragment Contact on contact_Entry {
        title
        heroImage {
            ...HeroTransform
        }
        textBody
        button {
            ...LinkField
        }

        contactMatrix {
            ...ContactMatrix
        }
        contentMatrix {
            ...ContentMatrix
        }

        settingsCookieConsent
        settingsMetaDescription
        settingsSeoTitle
        settingsCookieConsent

        localized {
            url
            language
        }
    }

    fragment ContactMatrix on contact2_Entry {
        whatsapp
        phone
        email
        messenger
        myTexel
        shop
    }

    fragment ContactGlobals on contact_GlobalSet {
        contactEmailText
        contactMessengerText
        contactMyTexelText
        contactPhoneText
        contactShopText
        contactWhatsappText
    }
`;

interface Props {
    data: ContactQuery;
}

const ContactBlocks = styled.ul`
    list-style: none;
    display: grid;
    grid-gap: 1.6rem;
    padding: 0;
    grid-template-columns: 1fr;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        grid-template-columns: 1fr 1fr;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) {
        grid-template-columns: 1fr 1fr 1fr;
        grid-gap: 3.2rem;
    }
`;

const Contact: FC<React.PropsWithChildren<Props>> = ({ data }) => {
    useAnalyticsContentCategory('contact');
    const { data: me, loading } = useContactMeQuery({
        errorPolicy: 'ignore',
    });
    const [showLoginModal, setShowLoginModal] = useState(false);
    const onMyTexelClick = useCallback(
        (e: React.MouseEvent) => {
            if (!me?.viewer?.id) {
                // assume not logged in
                e.preventDefault();
                setShowLoginModal(true);
            }
        },
        [me]
    );

    const { globalSet, entry } = data;

    if (globalSet?.__typename !== 'contact_GlobalSet' || entry?.__typename !== 'contact_Entry') {
        throw new Error('Not enough data to construct contact page');
    }

    const matrix = entry.contactMatrix?.[0];
    const heroImage = entry.heroImage?.[0];

    return (
        <Page>
            <Seo
                image={heroImage?.ogImageUrl ?? ''}
                settingsMetaDescription={entry.settingsMetaDescription ?? ''}
                settingsSeoTitle={entry.settingsSeoTitle ?? entry.title ?? ''}
                alternates={entry.localized}
            />
            {heroImage && <Hero {...heroTransformToProps(heroImage)} />}

            <ContentWrapper>
                <Box mt={[8]} mb={[3, 5]}>
                    <StyledBox>
                        <Title variant="large">{entry.title}</Title>
                        {entry.textBody && <HtmlMapper html={entry.textBody}>{tagMap}</HtmlMapper>}
                        {entry.button?.[0] && (
                            <Box mt={5} mb={[6, 7]}>
                                <LinkField link={entry.button[0]} style={{ textDecoration: 'none' }}>
                                    <Button size="large" rounded as="span" style={{ display: 'inline-flex' }}>
                                        <FormattedMessage defaultMessage="Veelgestelde vragen" />
                                    </Button>
                                </LinkField>
                            </Box>
                        )}
                    </StyledBox>

                    {matrix && (
                        <Box as={ContactBlocks} m={0} mt={7} mb={[6, 8]}>
                            {contactOptions.map(
                                (option, i) =>
                                    matrix[option.matrixKey] && (
                                        <ContactBlock key={i}>
                                            <Flex flexDirection="row" p={[5, 6]}>
                                                <StyledIconBox mr={[2, 3]}>{option.icon}</StyledIconBox>
                                                <Box>
                                                    <Title variant="small">{option.name}</Title>
                                                    <StyledBody>{globalSet[option.globalKey]}</StyledBody>

                                                    <Body>
                                                        {option.link ??
                                                            (loading ? (
                                                                <FormattedMessage defaultMessage="Laden..." />
                                                            ) : (
                                                                <MyLink onClick={onMyTexelClick}>
                                                                    {me?.viewer?.id ? (
                                                                        <FormattedMessage defaultMessage="Naar mijn-omgeving" />
                                                                    ) : (
                                                                        <FormattedMessage defaultMessage="Inloggen" />
                                                                    )}
                                                                </MyLink>
                                                            ))}
                                                    </Body>
                                                </Box>
                                            </Flex>
                                        </ContactBlock>
                                    )
                            )}
                        </Box>
                    )}
                </Box>
            </ContentWrapper>

            <LoginModal open={showLoginModal} onClose={() => setShowLoginModal(false)} />

            {entry.contentMatrix && (
                <Box mb={[7, 8]} pb={[0, 8]}>
                    <ContentMatrix
                        matrix={entry.contentMatrix.filter((field): field is ContentMatrixFragment => !!field)}
                        pageChildren={[]}
                        pageSiblings={[]}
                    />
                </Box>
            )}
        </Page>
    );
};

export const getStaticProps: GetStaticProps = async context => {
    if (context.locale === 'default') return { props: {} };
    const { previewData, params } = context;
    const client = initApolloClient(context.locale as string, { previewData: previewData as PreviewData });
    const lang = context.locale as string;

    const { data } = await client.query<ContactQuery, ContactQueryVariables>({
        query: ContactDocument,
        variables: { lang },
    });

    const suppressCookieConsent = !(
        (data.entry as { settingsCookieConsent?: boolean })?.settingsCookieConsent ?? false
    );

    if (!data.entry) {
        const redirect = await hasRedirect((params?.uri as string[]).join('/'), lang);

        if (redirect) {
            const { redirectDestUrl, redirectHttpCode } = redirect;
            return {
                redirect: {
                    permanent: redirectHttpCode === 301,
                    destination: redirectDestUrl,
                },
                revalidate: DEFAULT_REVALIDATION_TIME,
            };
        }

        return {
            notFound: true,
        };
    }

    return {
        ...addApolloState(
            client,
            (await addGlobalQuery(
                {
                    props: {
                        data,
                        suppressCookieConsent,
                    },
                },
                lang
            )) as { props: Record<string, unknown> }
        ),
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

const StyledBox = styled(Box)`
    max-width: 92.8rem;
    p {
        line-height: 1.5;
    }
`;

const ContactBlock = styled.li`
    background: ${({ theme }) => theme.colors.neutral['10']};
    display: flex;
    flex-direction: column;
    border-radius: 0.6rem;
    h3 {
        margin-bottom: 0.4rem;
    }
`;
const StyledIconBox = styled(Box)`
    max-width: 3.2rem;
    svg {
        margin-right: 1.6rem;
        @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) {
            width: 3.2rem;
            height: 3.2rem;
        }
    }
`;

const StyledBody = styled(Body)`
    line-height: 1.5;
    margin-bottom: 0.8rem;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) {
        min-height: 5.6rem;
        margin-bottom: 1.6rem;
    }
`;

export default Contact;
